import { Injectable } from '@angular/core';
import { ChildTableColumn, ChildTableConfig, ChildTableRow, AlertModalConfig } from '../models/child-table.models';
import { CoerceResult, normalizeRange } from '../core/models/cell.models';
import { ChildTableStateService } from './child-table-state.service';
import { ChildTableA11yService } from './child-table-a11y.service';
import { ChildTableSelectionService } from './child-table-selection.service';
import { ChildTableEditingService } from './child-table-editing.service';
import { ChildTableDndService } from './child-table-dnd.service';

/** Snapshot of cells affected by a paste, used for atomic undo. */
export interface PasteSnapshot {
  cells: Array<{ rowId: string; columnKey: string; previousValue: any }>;
  errorCells: Array<{ rowId: string; columnKey: string }>;
}

/** Data the component needs to show an alert after paste. */
export interface PasteAlertRequest {
  config: AlertModalConfig;
}

/**
 * Manages clipboard operations (copy, cut, paste) and the paste-error
 * lock/undo workflow for the child table grid.
 *
 * Component-scoped (provided via the host component's `providers` array).
 * UI concerns (alert modals, event emission) are returned as data for the
 * component to handle.
 */
@Injectable()
export class ChildTableClipboardService<T = any> {
  pasteErrorLocked = false;
  pasteSnapshot: PasteSnapshot | null = null;
  private pasteErrorModalTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly stateService: ChildTableStateService<T>,
    private readonly a11yService: ChildTableA11yService,
    private readonly selectionService: ChildTableSelectionService,
    private readonly editingService: ChildTableEditingService<T>,
    private readonly dndService: ChildTableDndService<T>,
  ) {}

  destroy(): void {
    if (this.pasteErrorModalTimer) {
      clearTimeout(this.pasteErrorModalTimer);
      this.pasteErrorModalTimer = null;
    }
  }

  /**
   * Copy the current cell range as TSV to the system clipboard.
   */
  onCopyAction(
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    displayRows: ChildTableRow<T>[],
    selectedIds: string[],
  ): void {
    const tsv = this.buildTsvFromRange(visibleRows, visibleColumns, displayRows, selectedIds);
    if (!tsv) return;
    navigator.clipboard.writeText(tsv).catch(() => {});
    this.a11yService.announce('Copied');
  }

  /**
   * Cut = copy + clear editable cells in the range.
   */
  onCutAction(
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    displayRows: ChildTableRow<T>[],
    selectedIds: string[],
    config: ChildTableConfig<T>,
  ): void {
    const tsv = this.buildTsvFromRange(visibleRows, visibleColumns, displayRows, selectedIds);
    if (!tsv) return;
    navigator.clipboard.writeText(tsv).catch(() => {});

    if (this.selectionService.cellRange) {
      const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.selectionService.cellRange);
      for (let r = minRow; r <= maxRow; r++) {
        const row = visibleRows[r];
        if (!row) continue;
        for (let c = minCol; c <= maxCol; c++) {
          const col = visibleColumns[c];
          if (!col?.editable || config.readonly) continue;
          if (col.type === 'file') continue;
          const clearVal = col.type === 'number' || col.type === 'currency' ? null : '';
          this.stateService.updateCell(row.id, col.key, clearVal);
        }
      }
    }
    this.a11yService.announce('Cut');
  }

  /**
   * Paste from clipboard starting at the given focus position.
   *
   * Returns an array of alert requests the component should display,
   * and an array of cellChange events the component should emit.
   */
  onPasteAction(
    event: ClipboardEvent,
    focusedRowIndex: number,
    focusedColIndex: number,
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    config: ChildTableConfig<T>,
    isMac: boolean,
    modKey: string,
    showAlertModal: (config: AlertModalConfig) => void,
    dismissAlertModal: () => void,
  ): void {
    if (this.editingService.editingCell) return;
    if (this.pasteErrorLocked) { event.preventDefault(); return; }

    const text = event.clipboardData?.getData('text/plain');
    if (!text) return;
    event.preventDefault();

    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter((l) => l.length > 0);
    const startRow = focusedRowIndex;
    const startCol = focusedColIndex;

    // Phase 1: snapshot all cells in the paste rectangle BEFORE modifying anything
    const snapshot: PasteSnapshot = { cells: [], errorCells: [] };
    let skippedFileColumns = false;
    for (let r = 0; r < lines.length; r++) {
      const row = visibleRows[startRow + r];
      if (!row) break;
      const cells = lines[r].split('\t');
      for (let c = 0; c < cells.length; c++) {
        const col = visibleColumns[startCol + c];
        if (!col?.editable || config.readonly) continue;
        if (col.type === 'file') { skippedFileColumns = true; continue; }
        snapshot.cells.push({
          rowId: row.id,
          columnKey: col.key,
          previousValue: (row.data as any)[col.key],
        });
      }
    }

    // Phase 2: apply paste and track errors
    let pastedCount = 0;
    let errorCount = 0;

    for (let r = 0; r < lines.length; r++) {
      const row = visibleRows[startRow + r];
      if (!row) break;
      const cells = lines[r].split('\t');
      for (let c = 0; c < cells.length; c++) {
        const col = visibleColumns[startCol + c];
        if (!col?.editable || config.readonly) continue;
        if (col.type === 'file') continue;

        const result = this.coerceValueForColumn(cells[c], col);
        if (result.error) {
          this.stateService.setCellError(row.id, col.key, result.error);
          snapshot.errorCells.push({ rowId: row.id, columnKey: col.key });
          errorCount++;
        } else {
          this.stateService.setCellError(row.id, col.key, null);
          this.stateService.updateCell(row.id, col.key, result.value);
          pastedCount++;
        }
      }
    }

    // Update range to cover the pasted area for visual feedback
    this.selectionService.cellRange = {
      anchorRow: startRow,
      anchorCol: startCol,
      focusRow: Math.min(startRow + lines.length - 1, visibleRows.length - 1),
      focusCol: Math.min(startCol + (lines[0]?.split('\t').length ?? 1) - 1, visibleColumns.length - 1),
    };

    if (errorCount > 0) {
      // Phase 3: atomic rollback — revert ALL valid cells so paste is all-or-nothing
      for (const cell of snapshot.cells) {
        const isError = snapshot.errorCells.some(
          (e) => e.rowId === cell.rowId && e.columnKey === cell.columnKey
        );
        if (!isError) {
          this.stateService.updateCell(cell.rowId, cell.columnKey, cell.previousValue);
        }
      }

      // Phase 4: engage paste-error lock
      this.pasteSnapshot = snapshot;
      this.pasteErrorLocked = true;
      this.dndService.pasteErrorLocked = true;
      this.pasteErrorModalTimer = setTimeout(() => {
        const modLabel = isMac ? `${modKey}+Z` : 'Ctrl+Z';
        showAlertModal({
          title: 'Unable to complete paste',
          message: 'Some pasted values don\'t match the expected format for their columns. Review the highlighted cells, then undo to continue editing.',
          buttonLabel: `Undo (${modLabel})`,
          icon: 'warning',
          onDismiss: () => this.undoBadPaste(dismissAlertModal),
        });
      }, 500);

      const modLabel = isMac ? 'Command+Z' : 'Control+Z';
      this.a11yService.announce(
        `Paste failed. ${errorCount} value${errorCount > 1 ? 's are' : ' is'} invalid. Press ${modLabel} to undo.`,
        'assertive'
      );
    } else if (pastedCount > 0) {
      this.a11yService.announce(`Pasted ${pastedCount} cells`);
    }

    if (skippedFileColumns && !this.pasteErrorLocked) {
      showAlertModal({
        title: 'File columns skipped',
        message: 'File Attachment columns cannot be updated by pasting text. Use the file upload dialog to attach files.',
        buttonLabel: 'OK',
        icon: 'info',
        onDismiss: () => dismissAlertModal(),
      });
    }
  }

  /**
   * Revert the last failed paste: clear error indicators, release the
   * paste-error lock, close the modal, and announce to screen readers.
   * Valid cells were already reverted atomically inside onPasteAction,
   * so we only need to clear the error flags here.
   */
  undoBadPaste(dismissAlertModal: () => void): void {
    if (!this.pasteSnapshot) return;
    for (const cell of this.pasteSnapshot.errorCells) {
      this.stateService.setCellError(cell.rowId, cell.columnKey, null);
    }
    this.pasteErrorLocked = false;
    this.dndService.pasteErrorLocked = false;
    this.pasteSnapshot = null;
    if (this.pasteErrorModalTimer) {
      clearTimeout(this.pasteErrorModalTimer);
      this.pasteErrorModalTimer = null;
    }
    dismissAlertModal();
    this.a11yService.announce('Paste undone. You can continue editing.', 'assertive');
  }

  /**
   * Build a TSV string from the current cell range rectangle.
   * Falls back to selected rows if no cell range.
   */
  buildTsvFromRange(
    visibleRows: ChildTableRow<T>[],
    visibleColumns: ChildTableColumn<T>[],
    displayRows: ChildTableRow<T>[],
    selectedIds: string[],
  ): string {
    // Primary: use cell range rectangle
    if (this.selectionService.cellRange) {
      const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.selectionService.cellRange);
      const lines: string[] = [];
      for (let r = minRow; r <= maxRow; r++) {
        const row = visibleRows[r];
        if (!row) continue;
        const vals: string[] = [];
        for (let c = minCol; c <= maxCol; c++) {
          const col = visibleColumns[c];
          if (!col) { vals.push(''); continue; }
          vals.push(String((row.data as any)[col.key] ?? ''));
        }
        lines.push(vals.join('\t'));
      }
      return lines.join('\n');
    }

    // Fallback: copy selected rows (checkbox selection)
    if (selectedIds.length > 0) {
      const colKeys = visibleColumns.map((c) => c.key);
      const lines: string[] = [];
      for (const rowId of selectedIds) {
        const row = displayRows.find((r) => r.id === rowId);
        if (!row) continue;
        lines.push(colKeys.map((k) => String((row.data as any)[k] ?? '')).join('\t'));
      }
      return lines.join('\n');
    }

    return '';
  }

  /**
   * Coerce a raw clipboard string into the appropriate type for a column.
   * Returns { value, error } — if error is set, the value should NOT be saved.
   */
  coerceValueForColumn(raw: string, column: ChildTableColumn<T>): CoerceResult {
    const trimmed = raw.trim();
    if (trimmed === '') {
      const emptyVal = column.type === 'number' || column.type === 'currency' ? null : '';
      return { value: emptyVal, error: null };
    }

    switch (column.type) {
      case 'number':
      case 'currency': {
        const cleaned = trimmed.replace(/[$,]/g, '');
        const num = Number(cleaned);
        if (Number.isNaN(num)) {
          return { value: null, error: `"${trimmed}" is not a valid number` };
        }
        return { value: num, error: null };
      }
      case 'select': {
        if (!column.selectOptions?.length) {
          return { value: trimmed, error: null };
        }
        const lower = trimmed.toLowerCase();
        const byValue = column.selectOptions.find((o) => String(o.value).toLowerCase() === lower);
        if (byValue) return { value: byValue.value, error: null };
        const byLabel = column.selectOptions.find((o) => o.label.toLowerCase() === lower);
        if (byLabel) return { value: byLabel.value, error: null };
        const allowed = column.selectOptions.map((o) => o.label).join(', ');
        return { value: null, error: `"${trimmed}" is not a valid option. Allowed: ${allowed}` };
      }
      case 'checkbox':
        return { value: trimmed === 'true' || trimmed === '1' || trimmed.toLowerCase() === 'yes', error: null };
      case 'date': {
        const dd = new Date(trimmed);
        if (isNaN(dd.getTime())) {
          return { value: null, error: `"${trimmed}" is not a valid date` };
        }
        return { value: trimmed, error: null };
      }
      case 'datetime': {
        const dt = new Date(trimmed);
        if (isNaN(dt.getTime())) {
          return { value: null, error: `"${trimmed}" is not a valid date/time` };
        }
        return { value: trimmed, error: null };
      }
      case 'email': {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(trimmed)) {
          return { value: null, error: `"${trimmed}" is not a valid email address` };
        }
        return { value: trimmed, error: null };
      }
      case 'phone': {
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length < 7 || digits.length > 15) {
          return { value: null, error: `"${trimmed}" is not a valid phone number` };
        }
        return { value: trimmed, error: null };
      }
      case 'multiselect': {
        if (!column.selectOptions?.length) {
          return { value: trimmed, error: null };
        }
        const options = trimmed.split(',').map(s => s.trim()).filter(Boolean);
        const validValues = column.selectOptions.map(o => String(o.value).toLowerCase());
        const validLabels = column.selectOptions.map(o => o.label.toLowerCase());
        const invalid = options.filter(o => !validValues.includes(o.toLowerCase()) && !validLabels.includes(o.toLowerCase()));
        if (invalid.length > 0) {
          return { value: null, error: `Invalid option(s): ${invalid.join(', ')}` };
        }
        const mapped = options.map(o => {
          const byVal = column.selectOptions!.find(opt => String(opt.value).toLowerCase() === o.toLowerCase());
          const byLabel = column.selectOptions!.find(opt => opt.label.toLowerCase() === o.toLowerCase());
          return String((byVal || byLabel)!.value);
        });
        return { value: mapped.join(','), error: null };
      }
      case 'formula':
        return { value: null, error: 'Formula fields are read-only and cannot be pasted into' };
      default:
        return { value: trimmed, error: null };
    }
  }
}
